// API caching service with TTL and invalidation strategies
/// <reference types="vite/client" />
/// <reference lib="dom" />

import { getSupabaseClient } from '@lumora/common';
import type { ApiResponse } from '@lumora/common';

import { API_BASE_URL } from '../config/env';
import {
  apiCache,
  CACHE_TTL,
  cacheApiResponse,
  getCachedApiResponse,
} from '../utils/cache';

interface CacheConfig {
  ttl: number;
  key: string;
  invalidateOn?: string[];
  skipCache?: boolean;
}

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

interface MarketQuote {
  symbol: string;
  price?: number;
  change?: number;
  changePercent?: number;
  volume?: number;
  marketCap?: number;
  pe?: number;
  [key: string]: unknown;
}

interface CachedApiResponse<T> extends ApiResponse<T> {
  fromCache: boolean;
  timestamp: number;
}

class ApiCacheService {
  private pendingRequests = new Map<string, Promise<unknown>>();

  // Generic cached fetch wrapper
  async cachedFetch<T>(
    url: string,
    options: RequestOptions = {},
    cacheConfig: Partial<CacheConfig> = {}
  ): Promise<CachedApiResponse<T>> {
    const config: CacheConfig = {
      ttl: CACHE_TTL.MEDIUM,
      key: this.generateCacheKey(url, options),
      skipCache: false,
      ...cacheConfig,
    };

    // Skip cache if requested or for non-GET requests
    if (config.skipCache || (options.method && options.method !== 'GET')) {
      const data = await this.fetchFromNetwork<T>(url, options);
      return {
        data,
        success: true,
        fromCache: false,
        timestamp: Date.now(),
      };
    }

    // Try to get from cache first
    const cachedData = getCachedApiResponse<T>(config.key);
    if (cachedData) {
      return {
        data: cachedData,
        success: true,
        fromCache: true,
        timestamp: Date.now(),
      };
    }

    // Check if request is already pending to avoid duplicate requests
    if (this.pendingRequests.has(config.key)) {
      const data = await (this.pendingRequests.get(config.key) as Promise<T>);
      return {
        data,
        success: true,
        fromCache: false,
        timestamp: Date.now(),
      };
    }

    // Fetch from network and cache
    const fetchPromise = this.fetchFromNetwork<T>(url, options);
    this.pendingRequests.set(config.key, fetchPromise);

    try {
      const data = await fetchPromise;
      cacheApiResponse(config.key, data, config.ttl);
      return {
        data,
        success: true,
        fromCache: false,
        timestamp: Date.now(),
      };
    } finally {
      this.pendingRequests.delete(config.key);
    }
  }

  // Portfolio-specific caching methods
  async getPortfolioSummary(
    profileId: string
  ): Promise<CachedApiResponse<unknown>> {
    return this.cachedFetch(
      `/api/portfolio/${profileId}/summary`,
      {},
      {
        ttl: CACHE_TTL.SHORT, // Portfolio data changes frequently
        key: `portfolio-summary-${profileId}`,
        invalidateOn: ['portfolio-update', 'holding-update'],
      }
    );
  }

  async getPortfolioHoldings(
    profileId: string
  ): Promise<CachedApiResponse<unknown[]>> {
    return this.cachedFetch(
      `/api/portfolio/${profileId}/holdings`,
      {},
      {
        ttl: CACHE_TTL.SHORT,
        key: `portfolio-holdings-${profileId}`,
        invalidateOn: ['portfolio-update', 'holding-update'],
      }
    );
  }

  async getPortfolioAnalytics(
    profileId: string,
    timeframe: string
  ): Promise<CachedApiResponse<unknown>> {
    return this.cachedFetch(
      `/api/analytics/${profileId}?timeframe=${timeframe}`,
      {},
      {
        ttl: CACHE_TTL.LONG, // Analytics data is heavy, cache for longer
        key: `analytics-${profileId}-${timeframe}`,
        invalidateOn: ['portfolio-update'],
      }
    );
  }

  async prefetchAnalytics(profileId: string): Promise<void> {
    // Prefetch common timeframes
    const timeframes = ['1Y', 'ALL'];
    await Promise.allSettled(
      timeframes.map(tf => this.getPortfolioAnalytics(profileId, tf))
    );
  }

  // Profile-specific caching methods
  async getUserProfile(userId: string): Promise<CachedApiResponse<unknown>> {
    return this.cachedFetch(
      `/api/profile/${userId}`,
      {},
      {
        ttl: CACHE_TTL.LONG, // Profile data changes less frequently
        key: `user-profile-${userId}`,
        invalidateOn: ['profile-update'],
      }
    );
  }

  async getUserProfiles(userId: string): Promise<CachedApiResponse<unknown[]>> {
    return this.cachedFetch(
      `/api/profile/${userId}/profiles`,
      {},
      {
        ttl: CACHE_TTL.MEDIUM,
        key: `user-profiles-${userId}`,
        invalidateOn: ['profile-update', 'profile-create'],
      }
    );
  }

  // Market data caching (external APIs)
  async getMarketData(symbols: string[]): Promise<ApiResponse<MarketQuote[]>> {
    // For now, fetch each symbol individually since Supabase Edge Functions
    // expect individual requests. In the future, we could modify the function
    // to accept multiple symbols.
    const results = await Promise.allSettled(
      symbols.map(symbol => this.getSingleQuote(symbol))
    );

    const successfulResults = results
      .filter(
        (result): result is PromiseFulfilledResult<MarketQuote> =>
          result.status === 'fulfilled'
      )
      .map(result => result.value);

    return {
      data: successfulResults,
      success: successfulResults.length > 0,
      message:
        successfulResults.length > 0
          ? 'Data retrieved successfully'
          : 'No data retrieved',
    };
  }

  private async getSingleQuote(symbol: string): Promise<MarketQuote> {
    const response = await fetch(`${API_BASE_URL}/market-quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await this.getSupabaseAccessToken()}`,
      },
      body: JSON.stringify({ symbol }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch quote for ${symbol}`);
    }

    return response.json();
  }

  // Helper method to get Supabase access token
  private async getSupabaseAccessToken(): Promise<string | null> {
    try {
      const supabase = getSupabaseClient();
      const { data } = await supabase.auth.getSession();
      const { session } = data;
      return session?.access_token || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  async getTickerInfo(symbol: string): Promise<CachedApiResponse<unknown>> {
    return this.cachedFetch(
      `/api/market/ticker/${symbol}`,
      {},
      {
        ttl: CACHE_TTL.VERY_LONG, // Ticker info rarely changes
        key: `ticker-info-${symbol}`,
      }
    );
  }

  // Cache invalidation methods
  invalidatePortfolioCache(profileId: string): void {
    const keysToInvalidate = [
      `portfolio-summary-${profileId}`,
      `portfolio-holdings-${profileId}`,
      `analytics-${profileId}`,
    ];

    keysToInvalidate.forEach(key => {
      apiCache.delete(key);
    });
  }

  invalidateProfileCache(userId: string): void {
    const keysToInvalidate = [
      `user-profile-${userId}`,
      `user-profiles-${userId}`,
    ];

    keysToInvalidate.forEach(key => {
      apiCache.delete(key);
    });
  }

  invalidateMarketCache(): void {
    // This would require pattern matching - for now clear all
    apiCache.clear();
  }

  // Preload critical data
  async preloadCriticalData(userId: string, profileId?: string): Promise<void> {
    const preloadPromises: Promise<unknown>[] = [this.getUserProfiles(userId)];

    if (profileId) {
      preloadPromises.push(
        this.getPortfolioSummary(profileId),
        this.getPortfolioHoldings(profileId)
      );
    }

    try {
      await Promise.allSettled(preloadPromises);
      console.log('Critical data preloaded');
    } catch (error) {
      console.warn('Failed to preload some critical data:', error);
    }
  }

  // Background refresh for stale data
  async refreshStaleData(): Promise<void> {
    // This would implement background refresh logic
    // For now, just clear expired items
    const stats = apiCache.getStats();
    console.log('Cache stats:', stats);
  }

  private async fetchFromNetwork<T>(
    url: string,
    options: RequestOptions
  ): Promise<T> {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private generateCacheKey(url: string, options: RequestOptions): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}-${url}-${body}`;
  }

  // Get cache statistics
  getCacheStats() {
    return {
      ...apiCache.getStats(),
      pendingRequests: this.pendingRequests.size,
    };
  }
}

// Export singleton instance
export const apiCacheService = new ApiCacheService();

// Hook for React components to use cached API calls
export function useCachedApi() {
  return {
    cachedFetch: apiCacheService.cachedFetch.bind(apiCacheService),
    getPortfolioSummary:
      apiCacheService.getPortfolioSummary.bind(apiCacheService),
    getPortfolioHoldings:
      apiCacheService.getPortfolioHoldings.bind(apiCacheService),
    getPortfolioAnalytics:
      apiCacheService.getPortfolioAnalytics.bind(apiCacheService),
    getUserProfile: apiCacheService.getUserProfile.bind(apiCacheService),
    getUserProfiles: apiCacheService.getUserProfiles.bind(apiCacheService),
    getMarketData: apiCacheService.getMarketData.bind(apiCacheService),
    getTickerInfo: apiCacheService.getTickerInfo.bind(apiCacheService),
    invalidatePortfolioCache:
      apiCacheService.invalidatePortfolioCache.bind(apiCacheService),
    invalidateProfileCache:
      apiCacheService.invalidateProfileCache.bind(apiCacheService),
    preloadCriticalData:
      apiCacheService.preloadCriticalData.bind(apiCacheService),
    prefetchAnalytics: apiCacheService.prefetchAnalytics.bind(apiCacheService),
    getCacheStats: apiCacheService.getCacheStats.bind(apiCacheService),
  };
}

// Cache warming strategies
export function warmCache(userId: string, profileId?: string): void {
  // Warm cache with critical data on app startup
  apiCacheService.preloadCriticalData(userId, profileId);
}

// Cache invalidation on mutations
export function invalidateCacheOnMutation(
  mutationType: string,
  entityId: string
): void {
  switch (mutationType) {
    case 'portfolio-update':
    case 'holding-update':
      apiCacheService.invalidatePortfolioCache(entityId);
      break;
    case 'profile-update':
    case 'profile-create':
      apiCacheService.invalidateProfileCache(entityId);
      break;
    case 'market-update':
      apiCacheService.invalidateMarketCache();
      break;
  }
}
